import { Component } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';

type CategoryKey =
    | 'blog'
    | 'additionalMaterials'
    | 'brochures'
    | 'newsletters'
    | 'pressRelease'
    | 'whitepapers';

interface ResourceItem {
    document: string;
    documentImage?: string;
    market: string;
    title: string;
    category: CategoryKey;
    updatedAt?: string;
}

interface ResourceData {
    blogTitle: string;
    blogDescription: string;
    additionalMaterialsTitle: string;
    additionalMaterialsDescription: string;
    brochuresTitle: string;
    brochuresDescription: string;
    newslettersTitle: string;
    newslettersDescription: string;
    pressReleaseTitle: string;
    pressReleaseDescription: string;
    whitepapersTitle: string;
    whitepapersDescription: string;
    resources: ResourceItem[];
}

// GraphQL response interfaces
interface MetaobjectFieldValue {
    value?: string;
}

interface MetaobjectNode {
    updatedAt?: string;
    title?: MetaobjectFieldValue;
    url?: MetaobjectFieldValue;
    imageurl?: MetaobjectFieldValue;
    region?: MetaobjectFieldValue;
    type?: MetaobjectFieldValue;
}

interface MetaobjectEdge {
    node: MetaobjectNode;
}

interface MetaobjectsResponse {
    data: {
        metaobjects: {
            edges: MetaobjectEdge[];
        };
    };
}

@Component
export class ResourceFiles extends ThemeComponent {
    private resourceFiles: HTMLScriptElement | null = null;
    private filesData: ResourceData | null = null;

    async init() {
        this.resourceFiles = document.getElementById('lac-resources-data') as HTMLScriptElement;
        if (!this.resourceFiles) { return; }

        // Parse the JSON data
        try {
            this.filesData = JSON.parse(this.resourceFiles.textContent || '{}') as ResourceData;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to parse resource data:', e);
            return;
        }

        await this.renderResourcesFromQuery();
    }

    /**
     * Read query param and render matching resources
     */
    async renderResourcesFromQuery() {
        if (!this.filesData) { return; }

        const params = new URLSearchParams(window.location.search);
        const category = params.get('category') as CategoryKey | null;
        if (!category) { return; }

        // Remove h1 & h2 if they exist
        const header = document.querySelector('.lac-resources__title');
        if (header) {
            const headers = header.querySelectorAll('h1, h2');
            for (const h of headers) {
                h.remove();
            }
        }

        // Hide categories wrapper
        const allCategoriesWrapper = document.querySelector('.lac-resources__categories');
        if (allCategoriesWrapper) {
            allCategoriesWrapper.classList.add('lac-resources__files--hidden');
        }

        // Get title and description
        const titleKey = `${category}Title` as keyof ResourceData;
        const descKey = `${category}Description` as keyof ResourceData;
        const categoryTitle = this.filesData[titleKey];
        const categoryDescription = this.filesData[descKey];

        // Insert title
        if (header && typeof categoryTitle === 'string') {
            const titleElement = document.createElement('h1');
            titleElement.innerHTML = categoryTitle;
            header.appendChild(titleElement);
        }

        // Insert description
        if (header && typeof categoryDescription === 'string') {
            const descElement = document.createElement('h2');
            descElement.classList.add('h2');
            descElement.innerHTML = categoryDescription;
            header.appendChild(descElement);
        }

        // Filter resources by category
        const filteredResources = this.filesData.resources.filter(
            (file) => file.category === category
        );

        const marketId = this.resourceFiles?.dataset.marketId;
        const metaObjectResults = await this.getMetaObjects(category, marketId);

        //eslint-disable-next-line no-console
        console.group('Debug Info: --------------------');
        //eslint-disable-next-line no-console
        console.log('Filtered Resources:', metaObjectResults);
        //eslint-disable-next-line no-console
        console.groupEnd();

        const filesSection = document.querySelector('.lac-resources__files');
        if (filesSection) {
            filesSection.classList.add('lac-resources__files--visible');
        }

        // Build list items
        // only open in new tab if link is document
        const isDocumentLink = (file: ResourceItem) => {
            const docExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx'];
            try {
                const url = new URL(file.document, window.location.origin);
                const pathname = url.pathname.toLowerCase();
                return docExtensions.some((ext) => pathname.endsWith(ext));
            } catch {
                // fallback for relative URLs or invalid URLs
                const lowerDoc = file.document.toLowerCase();
                return docExtensions.some((ext) => lowerDoc.endsWith(ext));
            }
        };

        // Need to combine the results from filesData and metaObjectResults
        const combinedResults = [...filteredResources, ...metaObjectResults]
            .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));

        const listItems = combinedResults
            .map((file) => {
                const formattedTitle = file.title.replace(/\[([^\]]+)\]/g, '<span>$1</span>');
                const imageHtml = file.documentImage
                    ? `<img src="${file.documentImage}" alt="${file.title}" class="resource-files__image" />`
                    : '';

                return `<li class="resource-files__item" data-market="${file.market}">

                    <a 
                        href="${file.document}" 
                        target="${isDocumentLink(file) ? '_blank' : '_self'}" 
                        rel="noopener noreferrer"
                    >
                        ${imageHtml}
                        <p>${formattedTitle}</p>
                    </a>
                </li>`;
            })
            .join('');

        // Insert into list
        const list = document.querySelector('.lac-resources__files ul#category-files');
        if (list) {
            list.innerHTML = listItems;
        }
    }

    async getMetaObjects(category?: string, marketId?: string | null): Promise<ResourceItem[]> {
        try {
            const headers = new Headers({
                /* eslint-disable */
                "Content-Type": "application/json",
                "X-Shopify-Storefront-Access-Token": "dde6019433de6c1c5ada02af082bf8e0"
                /* eslint-enable */
            });

            //  Fetch grouped products
            const brandObjects = fetch(
                `/api/2025-07/graphql.json`,
                {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({
                        query: `
                        query {
                            metaobjects(type: "lac_resources", first: 250) {
                                edges {
                                    node {
                                        updatedAt
                                        region: field(key: "region") {
                                            value
                                        }
                                        type: field(key: "type") {
                                            value
                                        }
                                        title: field(key: "title") {
                                            value
                                        }
                                        url: field(key: "url") {
                                            value
                                        }
                                        imageurl: field(key: "imageurl") {
                                            value
                                        }
                                    }
                                }
                            }
                        }
                    `
                    })
                }
            ).then(((res) => res.json()))
                .then((resources: MetaobjectsResponse) => {
                    const edges = resources.data.metaobjects.edges;
                    const categoryLower = category ? category.toLowerCase().replace(/\s+/g, '') : null;

                    const filteredEdges = edges.filter((edge) => {
                        const region = edge.node.region?.value ? edge.node.region.value.toLowerCase() : undefined;
                        const type = edge.node.type?.value?.toLowerCase().replace(/\s+/g, '');

                        return (!marketId || (region === marketId.toLowerCase())) &&
                            (!categoryLower || (type === categoryLower));
                    });

                    // Map filtered edges to ResourceItem[]
                    const resourceItems: ResourceItem[] = filteredEdges.map((edge) => ({
                        document: edge.node.url?.value || '',
                        documentImage: edge.node?.imageurl?.value || '',
                        market: edge.node.region?.value || '',
                        title: edge.node.title?.value || '',
                        category: (edge.node.type?.value?.toLowerCase() as CategoryKey) || 'blog',
                        updatedAt: edge.node.updatedAt
                    }));

                    return resourceItems;
                });

            return await brandObjects;

        } catch (error) {
            if (typeof error === "string") {
                throw Error(error);
            } else if (error instanceof Error) {
                throw Error(error.message);
            }
        }
        return [];
    }
}
